/**
 * Zanetti homepage behaviour: muted loop videos, the cap picker and the film modal.
 * Loaded once from snippets/zanetti-theme.liquid. Each part is a custom element or a
 * small observer, so sections re-rendered in the theme editor re-initialise themselves.
 */

/* Loop videos: attach the source only when the video is visible (the desktop and
   phone copies never both download), then play muted. A single play() call can be
   refused (page still loading, background tab, iOS Low Power Mode), so playback is
   retried when the video has data, when the page becomes visible or is restored, and on
   the visitor's first touch, click, key or scroll. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @param {HTMLVideoElement} video */
  const isShown = (video) => video.getClientRects().length > 0;

  /** @param {HTMLVideoElement} video */
  const tryPlay = (video) => {
    if (reduceMotion.matches || !video.paused || !video.getAttribute('src')) return;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  };

  /** @param {HTMLVideoElement} video */
  const prepare = (video) => {
    if (video.getAttribute('src') || !isShown(video)) return;
    const src = video.dataset.src;
    if (!src) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    // No autoplay attribute: when iOS Low Power Mode blocks it, Safari draws its own play
    // button over the film. play() below (and the retries) start it instead.
    video.addEventListener('loadeddata', () => tryPlay(video));
    video.addEventListener('canplay', () => tryPlay(video));
    video.src = src;
  };

  const scan = () => {
    document.querySelectorAll('video[data-z-loop]').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      prepare(video);
      if (isShown(video)) tryPlay(video);
    });
  };

  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  };

  const loopVideos = () =>
    [...document.querySelectorAll('video[data-z-loop]')].filter(
      (video) => video instanceof HTMLVideoElement && video.getAttribute('src')
    );

  /**
   * Plays even if the element claims to be playing: after the app has been backgrounded,
   * iOS can leave a video "playing" but frozen. If time still hasn't moved a moment after
   * a successful play(), the source is reloaded.
   * @param {HTMLVideoElement} video
   */
  const resume = (video) => {
    if (reduceMotion.matches || !isShown(video)) return;
    const before = video.currentTime;
    const attempt = video.play();
    if (!attempt || typeof attempt.then !== 'function') return;
    attempt
      .then(() => {
        window.setTimeout(() => {
          if (document.hidden || video.paused || video.readyState < 2) return;
          if (video.currentTime !== before) return;
          // Reload, then carry on from the same moment rather than the start.
          video.addEventListener(
            'loadedmetadata',
            () => {
              if (before > 0 && before < video.duration) video.currentTime = before;
            },
            { once: true }
          );
          video.load();
          const retry = video.play();
          if (retry && typeof retry.catch === 'function') retry.catch(() => {});
        }, 1500);
      })
      .catch(() => {});
  };

  const resumeAll = () => {
    scan();
    loopVideos().forEach(resume);
  };

  // Pause when the page is hidden (home screen, app switch, other tab) so the state is
  // clean, then restart when it comes back.
  const pauseAll = () => {
    loopVideos().forEach((video) => video.pause());
  };

  // Films start once the page has loaded, so they don't compete with the posters, photos
  // and fonts on first paint. Scrolling or a first tap starts them sooner.
  if (document.readyState === 'complete') {
    scan();
  } else {
    window.addEventListener('load', scan, { once: true });
  }
  window.addEventListener('pageshow', resumeAll);
  window.addEventListener('pagehide', pauseAll);
  window.addEventListener('focus', resumeAll);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resumeAll();
    } else {
      pauseAll();
    }
  });
  document.addEventListener('shopify:section:load', scan);

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(scan, 200);
  });

  // First interaction counts as a user gesture, which unlocks playback where autoplay was refused.
  for (const type of ['pointerdown', 'touchstart', 'keydown']) {
    document.addEventListener(type, resumeAll, { capture: true, passive: true });
  }
  for (const type of ['scroll', 'wheel']) {
    document.addEventListener(type, queueScan, { capture: true, passive: true });
  }
})();

/* Cap picker */
class ZCapPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-z-caps]');
    if (!data) return;
    try {
      this.caps = JSON.parse(data.textContent || '[]');
    } catch (error) {
      console.warn('[zanetti] Could not read cap data', error);
      return;
    }
    this.index = Number(this.dataset.start) || 0;
    this.view = 'front';

    this.stage = this.querySelector('.z-caps__stage');
    this.stageImg = this.querySelector('.z-caps__stage-img');
    this.nameEl = this.querySelector('[data-z-name]');
    this.noteEl = this.querySelector('[data-z-note]');
    this.priceEl = this.querySelector('[data-z-price]');
    this.addBtn = this.querySelector('[data-z-add]');
    this.variantInput = this.querySelector('[data-z-variant]');
    this.detailsLink = this.querySelector('[data-z-details]');
    this.statusEl = this.querySelector('[data-z-status]');
    this.form = this.querySelector('[data-z-cap-form]');

    this.addEventListener('click', this.onClick);
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const swatch = target?.closest('.z-caps__swatch');
    if (swatch instanceof HTMLElement && this.contains(swatch)) {
      this.index = Number(swatch.dataset.index) || 0;
      this.render();
      return;
    }
    const tab = target?.closest('.z-caps__view');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      this.view = tab.dataset.view || 'front';
      this.render();
    }
  };

  render() {
    const cap = this.caps?.[this.index];
    if (!cap) return;
    let view = this.view;
    if (!cap.views?.[view]) view = 'front';
    const image = cap.views?.[view];

    if (this.stageImg instanceof HTMLImageElement && image) {
      this.stageImg.removeAttribute('width');
      this.stageImg.removeAttribute('height');
      this.stageImg.srcset = image.srcset || '';
      this.stageImg.src = image.src;
      this.stageImg.alt = image.alt || '';
      this.stageImg.style.removeProperty('object-position');
    }
    this.stage?.classList.toggle('is-worn', view === 'worn');

    this.querySelectorAll('.z-caps__view').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const id = tab.dataset.view;
      tab.hidden = id === 'worn' && !cap.views?.worn;
      tab.setAttribute('aria-pressed', String(id === view));
    });
    this.querySelectorAll('.z-caps__swatch').forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;
      swatch.setAttribute('aria-pressed', String(Number(swatch.dataset.index) === this.index));
    });

    if (this.nameEl) this.nameEl.textContent = cap.name;
    if (this.noteEl) this.noteEl.textContent = cap.note || '';
    if (this.priceEl) this.priceEl.textContent = cap.price;
    if (this.variantInput instanceof HTMLInputElement) this.variantInput.value = String(cap.variantId);
    if (this.detailsLink instanceof HTMLAnchorElement) this.detailsLink.href = cap.url;
    if (this.addBtn instanceof HTMLButtonElement) {
      this.addBtn.disabled = !cap.available;
      this.addBtn.textContent = cap.available
        ? `${this.dataset.addLabel} — ${cap.price}`
        : this.dataset.soldOutLabel || 'Sold out';
    }
  }

  /** @param {SubmitEvent} event */
  onSubmit = async (event) => {
    const cap = this.caps?.[this.index];
    const actions = window.Shopify?.actions;
    if (!cap || !actions?.updateCart) return; // No JS cart API: let the form post to /cart/add.
    event.preventDefault();

    const button = this.addBtn;
    button?.setAttribute('aria-busy', 'true');
    try {
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: String(cap.variantId), quantity: 1 }] },
        { event: { context: 'product' } }
      );
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      if (this.statusEl) this.statusEl.textContent = this.dataset.addedLabel || '';
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      // @ts-ignore
      if (drawer && !drawer.isOpen) await actions.openCart?.();
    } catch (error) {
      console.warn('[zanetti] Add to bag failed, falling back to the form', error);
      this.form?.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('z-cap-picker')) customElements.define('z-cap-picker', ZCapPicker);

/* Film modal */
class ZFilmModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.video = this.querySelector('video');
    this.opener = this.querySelector('[data-z-film-open]');
    this.closer = this.querySelector('[data-z-film-close]');
    this.opener?.addEventListener('click', this.open);
    this.closer?.addEventListener('click', this.close);
    this.dialog?.addEventListener('close', this.onClose);
    this.dialog?.addEventListener('click', this.onBackdrop);
    // Some browsers and polyfills skip the dialog's close event, so also watch the open attribute.
    if (this.dialog) {
      this.openObserver = new MutationObserver(() => {
        if (this.dialog instanceof HTMLDialogElement && !this.dialog.open) this.onClose();
      });
      this.openObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    this.openObserver?.disconnect();
    this.opener?.removeEventListener('click', this.open);
    this.closer?.removeEventListener('click', this.close);
    this.dialog?.removeEventListener('close', this.onClose);
    this.dialog?.removeEventListener('click', this.onBackdrop);
  }

  open = () => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    const video = this.video;
    if (video instanceof HTMLVideoElement && !video.getAttribute('src') && video.dataset.src) {
      video.src = video.dataset.src;
    }
    this.dialog.showModal();
    this.opener?.setAttribute('aria-expanded', 'true');
    if (video instanceof HTMLVideoElement) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  };

  close = () => {
    if (this.dialog instanceof HTMLDialogElement && this.dialog.open) this.dialog.close();
  };

  onClose = () => {
    if (this.opener?.getAttribute('aria-expanded') !== 'true') return;
    if (this.video instanceof HTMLVideoElement) this.video.pause();
    this.opener?.setAttribute('aria-expanded', 'false');
    if (this.opener instanceof HTMLElement) this.opener.focus();
  };

  /** @param {MouseEvent} event */
  onBackdrop = (event) => {
    if (event.target === this.dialog) this.close();
  };
}
if (!customElements.get('z-film-modal')) customElements.define('z-film-modal', ZFilmModal);

/* The theme scrolls inside .page-wrapper, so bring hash targets (e.g. the sign-up
   thank-you after the form posts back) into view on load. */
(() => {
  const reveal = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ block: 'center', behavior: 'instant' });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reveal, { once: true });
  } else {
    reveal();
  }
})();
